import { Box, Container, styled } from "@mui/material";
import classnames from "classnames";
import React from 'react'
import PropTypes from 'prop-types'
import useRootScopeProperties from "../../hoc/hooks/useRootScopeProperties";
import background from "../../style/images/background.jpg";

import ContentHeader from './ContentHeader'
import ContentLine from './ContentLine'
import ContentPartner from './ContentPartner'
import ContentBack from './ContentBack'

// Modern, merkgebonden 'aurora'-verloop voor de homepage-hero (vervangt de foto).
// Puur CSS: kleurgloed-vlekken in paars/oranje/magenta + een fijn stippenraster.
const AURORA_BACKGROUND =
  "radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)," +
  "radial-gradient(48% 58% at 12% 18%, rgba(245,135,31,0.55) 0%, transparent 55%)," +
  "radial-gradient(44% 54% at 88% 22%, rgba(214,83,126,0.60) 0%, transparent 55%)," +
  "radial-gradient(58% 68% at 72% 92%, rgba(127,119,221,0.55) 0%, transparent 55%)," +
  "linear-gradient(135deg, #3A1838 0%, #50244F 50%, #2c1330 100%)";

const StyledHeaderWrap = styled('div')(({ theme }) => ({
	paddingTop: '88px', // This corrects for the default 'header height'
	backgroundColor: theme.palette.primary.main,
	backgroundPosition: 'center center',
	backgroundSize: 'cover',
	backgroundAttachment: 'scroll',

	'& > div': {
		position: 'relative',
		zIndex: '50',
	},

	'.no-webp &': {
		backgroundImage: `url('${background}')`,
	},

	'.webp &': {
		backgroundImage: `url('${background}')`,
	},

	// Aurora-variant: overschrijft de foto met het CSS-verloop (homepage-hero).
	'&.Content-aurora, &.Content-aurora.no-webp, &.Content-aurora.webp': {
		backgroundImage: AURORA_BACKGROUND,
		backgroundSize: '24px 24px, cover, cover, cover, cover',
		backgroundRepeat: 'repeat, no-repeat, no-repeat, no-repeat, no-repeat',
		backgroundPosition: 'top left, center, center, center, center',
		// Extra paarse ruimte onderaan; het 'Meer van Athena'-blok steekt hier overheen.
		paddingBottom: '120px',
		[theme.breakpoints.down('md')]: {
			paddingBottom: '80px',
		},
	},
}))

const PREFIX = 'Content';
export const contentClasses = {
	withoutHeader: `${PREFIX}-withoutHeader`,
	body: `${PREFIX}-body`,
	sheet: `${PREFIX}-sheet`,
	padding: `${PREFIX}-padding`,
	line: `${PREFIX}-line`,
	aurora: `${PREFIX}-contentAurora`,
}

const StyledContentDiv = styled('div')(({ theme }) => ({
	flex: 1,
	backgroundColor: theme.palette.common.white,
	zIndex: 50,

	[`&.${contentClasses.withoutHeader}`]: {
		marginTop: '176px',
	},

	// Aurora-homepage: de content-zone krijgt de grijze paginakleur zodat de
	// tussenruimtes tussen blokken grijs zijn. De overlap over de hero wordt
	// gedaan door het eerste blok ('Meer van Athena') in MeerVanAthena.jsx,
	// dat met een negatieve marge over de paarse hero heen steekt.
	[`&.${contentClasses.aurora}`]: {
		position: 'relative',
		zIndex: 51,
		backgroundColor: '#F4F4F6',
	},

	[`& .${contentClasses.body}`]: {
		marginTop: 0,
		[`&.${contentClasses.sheet}`]: {
			marginBottom: '30px',
			padding: '40px 0',
			minHeight: '320px',
		},
		[`&.${contentClasses.padding}`]: {
			[theme.breakpoints.up('xs')]: {
				padding: theme.spacing(3, 1),
				[`& .${contentClasses.line}`]: {
					marginLeft: '-30px',
					marginRight: '-30px',
				}
			},

			[theme.breakpoints.up('md')]: {
				padding: '20px 50px',

				[`& .${contentClasses.line}`]: {
					marginLeft: '-50px',
					marginRight: '-50px',
				},
			},
		},
	},
}))

const Content = React.forwardRef(({ children, noContainer, noSheet, padding, auroraBackground }, ref) => {
	let Header = null;
	let childrenNew = [];
	const { customBackgroundUrl } = useRootScopeProperties();

	if(Array.isArray(children)){
		children.map((child, i) => {
			if(child && child.type && (child.type.name == 'ContentHeader' || child.type == ContentHeader)){
				Header = children[i];
			}else{
				childrenNew.push(children[i]);
			}
		});
	}else{
		childrenNew = children;
	}

	// Bij de aurora-achtergrond negeren we de (custom) foto bewust.
	// Belangrijk: 'style' moet een object of undefined zijn, nooit false/'' .
	const customBackground = (!auroraBackground && customBackgroundUrl)
		? { backgroundImage: `url(${customBackgroundUrl})` }
		: undefined;

	return (
		<Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }} ref={ref}>
			<StyledHeaderWrap className={auroraBackground ? 'Content-aurora' : undefined} style={customBackground}>
				{Header}
			</StyledHeaderWrap>
			<StyledContentDiv className={classnames(!!Header ? '' : contentClasses.withoutHeader, { [contentClasses.aurora]: auroraBackground })}>
				{noContainer ? (
					<div className={classnames(contentClasses.body, {
						[contentClasses.sheet]: !noSheet,
						[contentClasses.padding]: padding,
					})}>
						{childrenNew}
					</div>
				) : (
					<Container>
						<div className={classnames(contentClasses.body, {
							[contentClasses.sheet]: !noSheet,
							[contentClasses.padding]: padding,
						})}>
							{childrenNew}
						</div>
					</Container>
				)}
			</StyledContentDiv>
		</Box>
	);
});

Content.Header = ContentHeader;
Content.Line = ContentLine;
Content.Partner = ContentPartner;
Content.Back = ContentBack;

Content.propTypes = {
	children: PropTypes.node.isRequired,
	noContainer: PropTypes.bool,
	noSheet: PropTypes.bool,
	padding: PropTypes.bool,
	auroraBackground: PropTypes.bool,
};

Content.defaultProps = {
	noContainer: false,
	noSheet: false,
	padding: false,
	auroraBackground: false,
};

export default Content
